import { meKeys } from '../lib/meQueryKeys.ts';
import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { companyApi, uploadFileToSignedUrl } from '@l2u/ui/lib/api.ts';
import { CenteredSpinner } from '@l2u/ui/components/Spinner.tsx';
import { EmptyState } from '@l2u/ui/components/EmptyState.tsx';
import { ErrorMessage } from '@l2u/ui/components/ErrorMessage.tsx';
import { Btn } from '@l2u/ui/components/Btn.tsx';
import { FileDrop } from '@l2u/ui/components/FileDrop.tsx';
import { DateField } from '@l2u/ui/components/DateField.tsx';
import { Modal } from '@l2u/ui/components/Modal.tsx';
import { FileHistoryModal } from '@l2u/ui/components/FileHistoryModal.tsx';
import type { AdoptionHistoryItem, FileHistoryItem } from '@l2u/ui/lib/fileHistory.ts';
import { expiryState, formatDateOnly, fromDateInput } from '@l2u/ui/lib/expiry.ts';
import { uploadDocStats, type UploadDocStats } from '../lib/docStats.ts';
import { complianceFilteredSubmissions } from '../lib/complianceUploads.ts';
import { UploadStateChip } from './UploadStateChip.tsx';
import { sortUploadRowsByQuestion } from '../lib/uploadRowOrder.ts';
import { complianceDocName } from '@l2u/ui/lib/complianceDocName.ts';
import { instanceOptionLabel } from '@l2u/ui/lib/instanceOption.ts';
import { DOCUMENT_ACCEPT } from '@l2u/ui/lib/fileTypes.ts';
import { documentAwaitingVendor } from '@l2u/ui/lib/documentsTab.ts';
import { currentConversation, documentsTabApprovals } from '@l2u/shared/threadPartition';
import { latestThreadMessage, messagePreviewText, visibleAt } from '@l2u/ui/lib/commentThread.ts';
import type {
  CompanyDocument,
  Question,
  QuestionCategory,
  Submission,
  SubmissionComment,
} from '@l2u/ui/lib/types.ts';
import { documentsNeedingAttention, liveFiles } from '../lib/documentsAttention.ts';
import { DocumentThreadModal } from './DocumentThreadModal.tsx';
import { FilePreviewModal } from '../components/FilePreviewModal.tsx';

export function MyDocuments() {
  const documentsQuery = useQuery({
    queryKey: meKeys.documents(),
    queryFn: () => companyApi.get<{ documents: CompanyDocument[] }>('/api/me/documents'),
  });
  const submissionsQuery = useQuery({
    queryKey: meKeys.submissions(),
    queryFn: () => companyApi.get<{ submissions: Submission[] }>('/api/me/submissions'),
  });
  const questionsQuery = useQuery({
    queryKey: meKeys.questions(),
    queryFn: () => companyApi.get<{ questions: Question[] }>('/api/me/questions'),
  });
  const categoriesQuery = useQuery({
    queryKey: meKeys.questionCategories(),
    queryFn: () =>
      companyApi.get<{ categories: QuestionCategory[] }>('/api/me/question-categories'),
  });
  // Every visible thread in the company in one request — the same query (key
  // and shape) the questionnaire runs, so the two share one cache entry.
  const threadsQuery = useQuery({
    queryKey: meKeys.commentThreads(),
    queryFn: () =>
      companyApi.get<{ threads: Record<string, SubmissionComment[]> }>('/api/me/comments'),
    retry: 1,
  });

  const documents = documentsQuery.data?.documents ?? [];
  const submissions = submissionsQuery.data?.submissions ?? [];
  const questions = questionsQuery.data?.questions ?? [];
  const categories = categoriesQuery.data?.categories ?? [];
  const threads = threadsQuery.data?.threads ?? {};

  if (
    documentsQuery.isLoading ||
    submissionsQuery.isLoading ||
    questionsQuery.isLoading ||
    categoriesQuery.isLoading ||
    threadsQuery.isLoading
  ) {
    return <CenteredSpinner />;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Compliance Documents</h1>
        <p className="mt-1 text-sm text-slate-500">
          Insurances &amp; Certifications you have uploaded.
        </p>
      </div>

      {/* A failed comments fetch would show every thread as empty, so say so
          and hide the Reply door rather than let the vendor reply blind. */}
      {threadsQuery.isError && (
        <div className="space-y-2">
          <ErrorMessage error={threadsQuery.error} />
          <button
            type="button"
            className="text-xs text-[#4D148C] underline hover:text-[#3B0F6B]"
            onClick={() => void threadsQuery.refetch()}
          >
            Retry
          </button>
        </div>
      )}

      <MyUploads
        submissions={submissions}
        questions={questions}
        categories={categories}
        documents={documents}
        threads={threads}
        threadsFailed={threadsQuery.isError}
      />
    </div>
  );
}

type FileRow = {
  kind: 'file';
  key: string;
  file_name: string;
  created_at: string;
  /** Owning submission — the History modal lists every version uploaded to it. */
  submissionId: string;
  questionId: string;
  questionLabel: string;
  optionKey: string | null;
  /** Human-readable option this file answers ("NSW"), for display only. Derived
   *  from the file's option_key or the owning question's instance option; kept
   *  separate from optionKey, which is echoed back to the upload API on re-upload. */
  optionLabel: string | null;
  fileId: string;
  expiresAt: string | null;
  mime: string;
  /** The owning question forces an expiry date on every upload (requires_expiry). */
  requiresExpiry: boolean;
};

type UploadRow =
  | FileRow
  | {
      kind: 'adoption';
      key: string;
      file_name: string;
      created_at: string;
      submissionId: string;
      questionId: string;
      questionLabel: string;
      optionKey: string | null;
      optionLabel: string | null;
      documentId: string;
      documentVersion: string | null;
      mime: string;
    };

// Standard documents only carry a storage path, so the preview modal infers
// how to render them from the extension. Uploaded files keep their true mime.
function mimeFromPath(path: string): string {
  const ext = path.split('.').pop()?.toLowerCase() ?? '';
  if (ext === 'pdf') return 'application/pdf';
  if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) {
    return `image/${ext === 'jpg' ? 'jpeg' : ext}`;
  }
  return 'application/octet-stream';
}

function MyUploads({
  submissions,
  questions,
  categories,
  documents,
  threads,
  threadsFailed,
}: {
  submissions: Submission[];
  questions: Question[];
  categories: QuestionCategory[];
  documents: CompanyDocument[];
  /** Visible comment threads keyed by submission id (meKeys.commentThreads()). */
  threads: Record<string, SubmissionComment[]>;
  /** The threads fetch failed: `threads` is empty, not known-empty, so no Reply or thread modal. */
  threadsFailed: boolean;
}) {
  const [preview, setPreview] = useState<UploadRow | null>(null);
  const [reupload, setReupload] = useState<FileRow | null>(null);
  const [history, setHistory] = useState<UploadRow | null>(null);
  const [thread, setThread] = useState<UploadRow | null>(null);
  const questionById = useMemo(() => new Map(questions.map((q) => [q.id, q])), [questions]);
  const docById = useMemo(() => new Map(documents.map((d) => [d.id, d])), [documents]);

  const complianceSubs = useMemo(
    () => complianceFilteredSubmissions(submissions, questionById),
    [submissions, questionById],
  );
  const subById = useMemo(() => new Map(complianceSubs.map((s) => [s.id, s])), [complianceSubs]);

  const now = new Date();
  const uploadStats = uploadDocStats(complianceSubs, now);
  // The same helper and the same three inputs as the Documents nav badge
  // (CompanyShell) and Home, so the group's document count always equals the
  // badge. It applies the Documents-tab filter itself, so it gets every
  // submission, not complianceSubs.
  const attentionIds = documentsNeedingAttention(submissions, questionById, docById, now);

  // Build per-category lists of uploaded files + adopted standard documents.
  const rowsByCategory = useMemo(() => {
    const map = new Map<string, UploadRow[]>();
    for (const cat of categories) map.set(cat.id, []);

    for (const s of complianceSubs) {
      const q = questionById.get(s.question_id);
      if (!q) continue;
      const bucket = map.get(q.category_id) ?? [];

      // liveFiles is the rule the badge's documentHasCard uses, so the cards
      // here and the documents counted there read the same files.
      for (const f of liveFiles(s)) {
        bucket.push({
          kind: 'file',
          key: `file:${f.id}`,
          file_name: f.file_name,
          created_at: f.created_at,
          submissionId: s.id,
          questionId: q.id,
          questionLabel: complianceDocName(q),
          optionKey: f.option_key,
          optionLabel: f.option_key ?? instanceOptionLabel(q, questionById),
          fileId: f.id,
          expiresAt: f.expires_at,
          mime: f.mime_type,
          requiresExpiry: q.requires_expiry,
        });
      }

      for (const a of s.submission_adoptions) {
        if (a.mode !== 'adopted') continue;
        const doc = docById.get(a.document_id);
        if (!doc) continue;
        bucket.push({
          kind: 'adoption',
          key: `adoption:${a.id}`,
          file_name: doc.name,
          created_at: a.acknowledged_at ?? a.created_at,
          submissionId: s.id,
          questionId: q.id,
          questionLabel: complianceDocName(q),
          optionKey: a.option_key,
          optionLabel: a.option_key ?? instanceOptionLabel(q, questionById),
          documentId: doc.id,
          documentVersion: doc.version ?? null,
          mime: mimeFromPath(doc.file_path),
        });
      }

      map.set(q.category_id, bucket);
    }

    // Mirror the questionnaire's order: sort each category's rows by the owning
    // question's display_order (created_at only breaks ties within a question).
    const displayOrderByQuestionId = new Map(questions.map((q) => [q.id, q.display_order]));
    for (const [k, list] of map) {
      map.set(k, sortUploadRowsByQuestion(list, displayOrderByQuestionId));
    }
    return map;
  }, [complianceSubs, questionById, docById, categories, questions]);

  const totalRows = useMemo(
    () => [...rowsByCategory.values()].reduce((n, list) => n + list.length, 0),
    [rowsByCategory],
  );

  if (totalRows === 0) {
    return (
      <EmptyState
        title="No compliant documents yet"
        description="Insurances & Certifications you upload will appear here once your reviewer marks them as compliant. To maintain ongoing compliance, all future insurance and certification renewals must be uploaded and managed in this section."
      />
    );
  }

  // Every card of a document needing attention moves to the group at the top
  // and leaves its category. The group keeps the page's order: categories in
  // turn, each already in sortUploadRowsByQuestion order. A submission's rows
  // all live in one category, so they stay together and in order.
  const attentionSet = new Set(attentionIds);
  const attentionRows = [...rowsByCategory.values()]
    .flat()
    .filter((r) => attentionSet.has(r.submissionId));
  const categorySections = categories
    .map((cat) => ({
      cat,
      rows: (rowsByCategory.get(cat.id) ?? []).filter((r) => !attentionSet.has(r.submissionId)),
    }))
    .filter((s) => s.rows.length > 0);

  // One thread per document: its preview and Reply go on the document's first
  // card in render order (group, then categories) and nowhere else.
  const firstRowKeyBySubmission = new Map<string, string>();
  for (const row of [...attentionRows, ...categorySections.flatMap((s) => s.rows)]) {
    if (!firstRowKeyBySubmission.has(row.submissionId)) {
      firstRowKeyBySubmission.set(row.submissionId, row.key);
    }
  }

  function renderCard(row: UploadRow) {
    const sub = subById.get(row.submissionId);
    // Only the conversation since the latest approval: a compliant document
    // starts fresh, and History carries the earlier ones.
    const approvals = documentsTabApprovals(questionById.get(row.questionId), sub);
    const comments = currentConversation(threads[row.submissionId] ?? [], approvals);
    const isFirst = firstRowKeyBySubmission.get(row.submissionId) === row.key;
    const awaiting = sub ? documentAwaitingVendor(sub) : false;
    const showPreview = !threadsFailed && isFirst && comments.length > 0;
    const showReply = !threadsFailed && isFirst && (comments.length > 0 || awaiting);
    // A renewal waiting on the reviewer: the expiry chip would describe the
    // file, not where the document stands.
    const underReview =
      sub !== undefined && sub.reverify_requested_at != null && sub.status !== 'rejected';

    const state = row.kind === 'file' ? expiryState({ expires_at: row.expiresAt }, now) : null;
    const border =
      state === 'expired'
        ? 'border-red-300'
        : state === 'expiring'
          ? 'border-amber-300'
          : 'border-slate-200';
    return (
      <div key={row.key} className={`rounded-xl border bg-white p-4 ${border}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div
              className="truncate text-sm font-semibold"
              style={{ color: 'var(--brand-primary)' }}
            >
              {row.file_name}
            </div>
            <div className="mt-0.5 flex items-center gap-2 text-xs text-slate-500">
              <span className="truncate">
                {row.questionLabel}
                {row.kind === 'adoption' && row.documentVersion ? ` · v${row.documentVersion}` : ''}
              </span>
              {row.optionLabel && (
                <span className="shrink-0 rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-normal text-indigo-700">
                  For: {row.optionLabel}
                </span>
              )}
            </div>
          </div>
          {row.kind === 'adoption' ? (
            <span className="shrink-0 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
              Adopted
            </span>
          ) : underReview ? (
            <span className="shrink-0 rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700">
              Under review
            </span>
          ) : (
            <UploadStateChip expiresAt={row.expiresAt} now={now} />
          )}
        </div>

        <div className="mt-3 grid grid-cols-2 gap-3 border-t border-slate-100 pt-3">
          <div>
            <div className="text-[11px] text-slate-400">Uploaded</div>
            <div className="text-xs font-medium text-slate-700">
              {new Date(row.created_at).toLocaleDateString()}
            </div>
          </div>
          <div>
            <div className="text-[11px] text-slate-400">Expiry</div>
            <div
              className={`text-xs font-medium ${
                state === 'expired'
                  ? 'text-red-600'
                  : state === 'expiring'
                    ? 'text-amber-600'
                    : 'text-slate-700'
              }`}
            >
              {row.kind === 'file' && row.expiresAt ? formatDateOnly(row.expiresAt) : '—'}
            </div>
          </div>
        </div>

        {showPreview && <ThreadPreview comments={comments} onOpen={() => setThread(row)} />}

        <div className="mt-3 flex gap-2">
          <Btn variant="secondary" size="sm" className="flex-1" onClick={() => setPreview(row)}>
            View File
          </Btn>
          <Btn variant="secondary" size="sm" className="flex-1" onClick={() => setHistory(row)}>
            History
          </Btn>
          {showReply && (
            <span className="relative flex-1">
              <Btn variant="secondary" size="sm" className="w-full" onClick={() => setThread(row)}>
                Reply
              </Btn>
              {awaiting && (
                <span
                  className="pointer-events-none absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-white"
                  aria-hidden="true"
                />
              )}
            </span>
          )}
          {row.kind === 'file' && (
            <Btn size="sm" className="flex-1" onClick={() => setReupload(row)}>
              Re-upload
            </Btn>
          )}
        </div>
      </div>
    );
  }

  const threadSub = thread ? subById.get(thread.submissionId) : undefined;
  const threadQuestion = thread ? questionById.get(thread.questionId) : undefined;

  return (
    <div className="space-y-6">
      <SummaryChips stats={uploadStats} />
      {attentionRows.length > 0 && (
        <section>
          <div className="mb-2 flex items-baseline justify-between">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-red-600">
              Needs your attention
            </h2>
            <span className="text-xs text-slate-500">
              {attentionIds.length} document{attentionIds.length === 1 ? '' : 's'}
            </span>
          </div>
          <div className="space-y-2">{attentionRows.map(renderCard)}</div>
        </section>
      )}
      {categorySections.map(({ cat, rows }) => (
        <section key={cat.id}>
          <div className="mb-2 flex items-baseline justify-between">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              {cat.name}
            </h2>
            <span className="text-xs text-slate-500">
              {rows.length} item{rows.length === 1 ? '' : 's'}
            </span>
          </div>
          <div className="space-y-2">{rows.map(renderCard)}</div>
        </section>
      ))}

      {preview && <UploadPreviewModal row={preview} onClose={() => setPreview(null)} />}
      {reupload && <ReuploadModal row={reupload} onClose={() => setReupload(null)} />}
      {history && (
        <DocumentHistoryModal
          submissionId={history.submissionId}
          questionLabel={history.questionLabel}
          thread={threads[history.submissionId] ?? []}
          // A failed threads fetch leaves `threads` empty, not known-empty:
          // fall back to the plain file list rather than show approval
          // markers with the conversations silently missing.
          approvals={
            threadsFailed
              ? null
              : documentsTabApprovals(
                  questionById.get(history.questionId),
                  subById.get(history.submissionId),
                )
          }
          onClose={() => setHistory(null)}
        />
      )}
      {thread && threadSub && threadQuestion && !threadsFailed && (
        <DocumentThreadModal
          submission={threadSub}
          question={threadQuestion}
          comments={currentConversation(
            threads[thread.submissionId] ?? [],
            documentsTabApprovals(threadQuestion, threadSub),
          )}
          onClose={() => setThread(null)}
        />
      )}
    </div>
  );
}

// The latest visible message of a document's thread, cut to two lines. The
// whole box opens the conversation, the same as the Reply button.
function ThreadPreview({
  comments,
  onOpen,
}: {
  comments: SubmissionComment[];
  onOpen: () => void;
}) {
  const latest = latestThreadMessage(comments);
  if (!latest) return null;
  return (
    <button
      type="button"
      onClick={onOpen}
      className="mt-3 w-full rounded-lg border border-brand-orange-border bg-brand-orange-tint px-3 py-2 text-left"
    >
      <div className="mb-1 flex items-center gap-2">
        {latest.author_type === 'admin' ? (
          <span className="rounded bg-brand-orange px-2 py-0.5 text-[11px] font-bold text-white">
            Reviewer
          </span>
        ) : (
          <span
            className="rounded px-2 py-0.5 text-[11px] font-bold text-white"
            style={{ background: 'var(--brand-primary)' }}
          >
            You
          </span>
        )}
        {/* Vendor-visible time: an admin comment's publish time, not its draft time. */}
        <span className="text-[11px] text-slate-500">
          {new Date(visibleAt(latest)).toLocaleDateString()}
        </span>
      </div>
      <div className="line-clamp-2 text-sm text-slate-800">{messagePreviewText(latest)}</div>
    </button>
  );
}

// Every version uploaded against this document — the same list the admin sees
// in their Compliance Documents tab, read from the vendor's own endpoint. A
// partitioned document (approvals !== null) also gets its thread, so History
// can show the conversations from before the latest approval.
function DocumentHistoryModal({
  submissionId,
  questionLabel,
  thread,
  approvals,
  onClose,
}: {
  submissionId: string;
  questionLabel: string;
  /** This submission's whole visible thread, from meKeys.commentThreads(). */
  thread: SubmissionComment[];
  /** documentsTabApprovals for this document; null = not partitioned, no thread in History. */
  approvals: string[] | null;
  onClose: () => void;
}) {
  const historyQuery = useQuery({
    queryKey: meKeys.filesHistory(submissionId),
    queryFn: () =>
      companyApi.get<{ files: FileHistoryItem[]; adoptions: AdoptionHistoryItem[] }>(
        `/api/me/submissions/${submissionId}/files/history`,
      ),
  });
  return (
    <FileHistoryModal
      questionLabel={questionLabel}
      loading={historyQuery.isLoading}
      error={historyQuery.isError ? historyQuery.error : null}
      files={historyQuery.data?.files ?? []}
      adoptions={historyQuery.data?.adoptions ?? []}
      {...(approvals !== null
        ? {
            thread: {
              comments: thread,
              approvals,
              originalNote: 'The conversation from the original review is on the questionnaire.',
            },
          }
        : {})}
      onClose={onClose}
    />
  );
}

function SummaryChips({ stats }: { stats: UploadDocStats }) {
  const cards = [
    { label: 'Current', value: stats.current, text: 'text-emerald-600' },
    { label: 'Expiring', value: stats.expiring, text: 'text-amber-600' },
    { label: 'Expired', value: stats.expired, text: 'text-red-600' },
  ];
  return (
    <div className="grid grid-cols-3 gap-3">
      {cards.map((c) => {
        const box =
          c.label === 'Current' && c.value > 0
            ? 'border-emerald-200 bg-emerald-50'
            : c.label === 'Expiring' && c.value > 0
              ? 'border-amber-300 bg-amber-50'
              : c.label === 'Expired' && c.value > 0
                ? 'border-red-200 bg-red-50'
                : 'border-slate-200 bg-white';
        return (
          <div key={c.label} className={`rounded-xl border p-4 text-center ${box}`}>
            <div className={`text-xl font-bold ${c.text}`}>{c.value}</div>
            <div className="text-xs text-slate-500">{c.label}</div>
          </div>
        );
      })}
    </div>
  );
}

// Preview an uploaded or adopted document (the shared FilePreviewModal).
function UploadPreviewModal({ row, onClose }: { row: UploadRow; onClose: () => void }) {
  return (
    <FilePreviewModal
      fileName={row.file_name}
      mimeType={row.mime}
      downloadPath={
        row.kind === 'file'
          ? `/api/me/submissions/${row.questionId}/files/${row.fileId}/download-url`
          : `/api/me/documents/${row.documentId}/download-url`
      }
      cacheKey={row.key}
      onClose={onClose}
    />
  );
}

// Replace an uploaded file: pick a new file, optionally set its expiry date,
// then upload + register it and supersede the old version (the same soft
// delete the wizard's Remove uses, so the admin file history keeps the
// replaced version).
function ReuploadModal({ row, onClose }: { row: FileRow; onClose: () => void }) {
  const qc = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [expiry, setExpiry] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  // Questions flagged requires_expiry must never register a dateless upload —
  // mirrors the wizard's compose step and the comment path's server rule.
  const missingExpiry = row.requiresExpiry && !expiry;

  async function upload() {
    if (!file || busy) return;
    if (missingExpiry) {
      setError(new Error('This document requires an expiry date.'));
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const mime = file.type || 'application/octet-stream';
      const presigned = await companyApi.post<{ path: string; url: string }>(
        `/api/me/submissions/${row.questionId}/upload-url`,
        { file_name: file.name, mime_type: mime },
      );
      await uploadFileToSignedUrl(presigned.url, file);
      await companyApi.post(`/api/me/submissions/${row.questionId}/files/${row.fileId}/replace`, {
        file_path: presigned.path,
        file_name: file.name,
        size_bytes: file.size,
        mime_type: mime,
        option_key: row.optionKey ?? undefined,
        expires_at: fromDateInput(expiry) ?? undefined,
      });
      await qc.invalidateQueries({ queryKey: meKeys.submissions() });
      await qc.invalidateQueries({ queryKey: meKeys.all });
      onClose();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      title={`Re-upload — ${row.file_name}`}
      onClose={onClose}
      footer={
        <>
          <Btn size="sm" variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Btn>
          <Btn
            size="sm"
            onClick={() => void upload()}
            disabled={!file || missingExpiry}
            loading={busy}
          >
            Upload & replace
          </Btn>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-slate-500">
          The new file replaces the current one for “{row.questionLabel}
          {row.optionLabel ? ` · ${row.optionLabel}` : ''}”.
        </p>
        <FileDrop
          multiple={false}
          accept={DOCUMENT_ACCEPT}
          disabled={busy}
          onFiles={(list) => setFile(list.item(0))}
        />
        {file && (
          <div className="rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-700">
            📎 {file.name}
          </div>
        )}
        <div className="text-sm">
          <span className="font-medium text-slate-700">
            Expiry date
            {row.requiresExpiry && <span className="ml-0.5 text-red-600">*</span>}
          </span>
          <div className="mt-1">
            <DateField
              value={expiry}
              onChange={setExpiry}
              disabled={busy}
              ariaLabel="Expiry date"
              placeholder={row.requiresExpiry ? 'Expiry date required' : 'No expiry'}
              invalid={missingExpiry && !!file}
            />
          </div>
          {row.requiresExpiry ? (
            <span
              className={`mt-1 block text-xs ${missingExpiry && file ? 'font-medium text-red-600' : 'text-slate-400'}`}
            >
              This document requires an expiry date.
            </span>
          ) : (
            <span className="mt-1 block text-xs text-slate-400">
              Leave blank if this document does not expire.
            </span>
          )}
        </div>
        <ErrorMessage error={error} />
      </div>
    </Modal>
  );
}
